import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button, Tabs, Pagination } from "@life-line-lab/components-react";
import { omitBy, isNil, isEmpty } from "lodash";

// SHARED
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import StatisticBox from "Components/shared/statistic-box";

// DRIVER COMPONENTS
import { DriverList } from "Components/new-driver-management/driver-list";
import { DriverProfile, DriverProfileTwo } from "Components/new-driver-management/driver-profile";

// CONSTANT
import { DEFAULT_MAX_PER_PAGE } from "Components/shared/constants";

// HOOKS
import { useItemsPerPage, useSession, useQuery, useAccessControlList } from "Hooks/index";
import usePagination from "Hooks/pagination.hook";

// HELPERS
import { decode, encode, toaster, toNum } from "Helpers/index";

// ANTD
import { Col, Row } from "antd";
import { RightCircleOutlined, ShoppingCartOutlined } from "@ant-design/icons";

// TYPES
import { DriverApprovalStatus } from "Types/new_driver_types";
import { Option } from "Types/common.types";

// SCSS
import "./active-driver.scss";

// SERVICES
import {
  driverActivation,
  driverApproval,
  fetchDiolkoCompaniesList,
  fetchDriver,
  fetchDriverList,
  fetchLogisticProviderList,
  fetchDriverStatisticCount,
} from "Services/new-driver-management.services";
import { AccountRole, Statuses } from "Types/common.types";
import { driverStats } from "Recoil/driver.atom";
import { useRecoilState, useRecoilValue } from "recoil";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";

const DriverListing = () => {
  const driverStatistics = [
    { name: "online", tab: "0", iconSrc: "/assets/icons/user-online.svg" },
    { name: "offline", tab: "1", iconSrc: "/assets/icons/user-offline.svg" },
    { name: "active", tab: "2", iconSrc: "/assets/icons/active-user.svg" },
    { name: "unapproved", tab: "3", iconSrc: "/assets/icons/needApproval.svg" },
  ];
  let initialFilters = {};
  const history = useHistory();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const query = useQuery();
  const { org, roleTypes } = useSession();

  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Rider_Management",
  });

  // permissions
  const canList = permission.rights.includes("LIST");
  const canView = permission.rights.includes("VIEW");
  const canCreate = permission.rights.includes("CREATE");
  const canEdit = permission.rights.includes("EDIT");
  const canDelete = permission.rights.includes("DELETE");

  const driverID = decode(id) || "";
  const tabKey = query.get("tabKey") || "0";
  const page = query.get("page") || 1;

  const [count, setCount] = useState(0);
  const [statResponse, setStatResponse] = useState<any>();
  const [tabActiveKey, setTabActiveKey] = useState(tabKey);
  const [forceRender, setForceRender] = useState<boolean>(false);

  const initialQueryParams = { page, offset: (toNum(page) - 1) * toNum(itemPerPage) };

  const [filters, setFilters] = useState<{ [key: string]: string | boolean | undefined }>(
    initialFilters,
  );
  const [approvalStatus, setApprovalStatus] = useState<DriverApprovalStatus>(
    DriverApprovalStatus.APPROVED,
  );
  const [isOnline, setIsOnline] = useState<boolean | undefined>();
  const [driverDetails, setDriverDetails] = useState<any>({});
  const [driverList, setDriverList] = useState<any>([]);
  const [companiesOption, setCompaniesOption] = useState<Option[]>([]);
  const globalFilterValue = useRecoilValue(GlobalFilterAtom);
  const [selectedOrgIDs, setSelectedOrgIDs] = useState<string[]>([org?._id]);

  const [currentListCount, setCurrentListCount] = useState(0);
  const { paginationComponent, itemPerPage, queryParams, setQueryParams } = usePagination({
    currentListCount,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const commonProps = {
    canList,
    canView,
    canEdit,
    canDelete,
    driverList,
    companiesOption,
    isLoading,
    onViewProfile(profileID: string) {
      const id = encode(profileID);
      history.push(`/driver-management/driver/${id}/profile`);
    },
    onDeactivateDriver(profileID, toDelete, reason) {
      handleDeactivateDriver(profileID, toDelete, reason);
    },
    onApproveRejectDriver(profileID, status, reason) {
      handleDriverApproval(profileID, status, reason);
    },
    onFilter(input) {
      setFilters({
        ...filters,
        orFields: input.orFields,
        vehicleType: input.vehicleType,
        companiesFilter: input.companiesFilter,
        riderType: input.driverType,
      });
    },
  };

  const tabKeyPaginationProps = {
    tabKey: tabKey,
    pathWithPage: {
      withTabKey: `/driver-management/driver?tabKey=${tabKey}&page=${page}`,
      withoutTabKey: `/driver-management/driver?page=${page}`,
    },
    pathWithoutPage: {
      withTabKey: `/driver-management/driver?tabKey=${tabKey}`,
      withoutTabKey: "/driver-management/driver",
    },
  };

  // list of different components shown in table
  const tabs = [
    {
      tabKey: "0",
      label: t("tab.onlineDriver"),
      component: (
        <DriverList tab={0} {...commonProps} />
      ),
    },
    {
      tabKey: "1",
      label: t("tab.offlineDriver"),
      component: (
        <DriverList tab={1} {...commonProps} />
      ),
    },
    {
      tabKey: "2",
      label: t("tab.activeDriver"),
      component: (
        <DriverList tab={2} {...commonProps} />
      ),
    },
    {
      tabKey: "3",
      label: t("tab.unapprovedDriver"),
      component: (
        <DriverList tab={3} {...commonProps} />
      ),
    },
    {
      tabKey: "4",
      label: t("tab.unverifiedDriver"),
      component: (
        <DriverList tab={4} {...commonProps} />
      ),
    },
    {
      tabKey: "5",
      label: t("tab.rejectedDriver"),
      component: (
        <DriverList tab={5} {...commonProps} />
      ),
    },
  ];

  useEffect(() => {
    if (!driverID) {
      handleFetchDriverList();
    }
  }, [queryParams.offset, filters, approvalStatus, isOnline, itemPerPage]);

  useEffect(() => {
    if (globalFilterValue.__isInitializing__) return;
    if (!isEmpty(globalFilterValue.companyIds) || !isEmpty(globalFilterValue.logisticsProviderIds))
      handleFetchDriverStats();
    //To send the orgIDs as array to backend
    let orgIDs: string[] = [];
    if (!isEmpty(globalFilterValue.companyIds)) {
      globalFilterValue?.companyIds?.map((orgSelected) => {
        orgIDs.push(orgSelected);
      });
    }
    setSelectedOrgIDs(orgIDs);
    if (!isEmpty(globalFilterValue.logisticsProviderIds)) {
      globalFilterValue?.logisticsProviderIds?.map((orgSelected) => {
        orgIDs.push(orgSelected);
      });
    }
    setSelectedOrgIDs(orgIDs);
  }, [globalFilterValue]);

  useEffect(() => {
    getStatCount();
  }, [selectedOrgIDs]);

  useEffect(() => {
    if (driverID) {
      handleFetchDriverDetails();
    }
  }, [driverID]);

  // fetch LP and companies list to generate options for table filters
  useEffect(() => {
    if (org.orgType == AccountRole.OWNER) handleFetchLogisticProviderList();
    if (org.orgType == AccountRole.LOGISTIC_PROVIDER || org.orgType == AccountRole.OWNER)
      handleFetchCompaniesList();
    // * to initialize state, the best way is write it inside the useState() function instead of manually setting the state through useEffect() which is much more prone to bugs
    // let orgIDs:string[] = [];
    // orgIDs.push(org._id);
    // setSelectedOrgIDs(orgIDs);
  }, []);

  const getStatCount = async () => {
    const response = await fetchDriverStatisticCount(selectedOrgIDs);
    setStatResponse(response);
  };

  useEffect(() => {
    switch (tabActiveKey) {
      case "0": {
        setApprovalStatus(DriverApprovalStatus.APPROVED);
        setIsOnline(true);
        break;
      }
      case "1": {
        setApprovalStatus(DriverApprovalStatus.APPROVED);
        setIsOnline(false);
        break;
      }
      case "2": {
        setApprovalStatus(DriverApprovalStatus.APPROVED);
        setIsOnline(undefined);
        break;
      }
      case "3": {
        setApprovalStatus(DriverApprovalStatus.UNAPPROVED);
        setIsOnline(undefined);
        break;
      }
      case "4": {
        setApprovalStatus(DriverApprovalStatus.PENDING);
        setIsOnline(undefined);
        break;
      }
      case "5": {
        setApprovalStatus(DriverApprovalStatus.REJECTED);
        setIsOnline(undefined);
        break;
      }
    }
  }, [tabActiveKey]);

  /**
   * function to shift tab to same status as statistic box
   * @param {string} key - tabKey
   */
  const handleStatClick = (key: string) => {
    let path = "";

    if (key === "0") {
      path = page ? `/driver-management/driver?page=${page}` : `/driver-management/driver`;
    } else {
      path = page
        ? `/driver-management/driver?tabKey=${key}&page=${page}`
        : `/driver-management/driver?tabKey=${key}`;
    }

    setTabActiveKey(key);
    setForceRender(true);
    history.push(path);
  };

  const handleCreateDriverAction = () => {
    history.push("/driver-management/driver/create");
  };

  const handleCreateExternalDriverAction = () => {
    history.push("/driver-management/driver/create/external");
  };

  /**
   * function to change url path when tab changes
   * @param {string} key - tabKey
   */
  const handleTabClick = (key: string) => {
    let path = "";

    if (key === "0") {
      path = page ? `/driver-management/driver?page=${page}` : `/driver-management/driver`;
    } else {
      path = page
        ? `/driver-management/driver?tabKey=${key}&page=${page}`
        : `/driver-management/driver?tabKey=${key}`;
    }

    setTabActiveKey(key);
    setForceRender(true);
    history.push(path);
  };

  // ! Not implemented
  // ! fetch companies for table filter options
  const handleFetchLogisticProviderList = async () => {
    const responseData = await fetchLogisticProviderList(org._id);
    if (responseData && !responseData.error) {
      // set LP option atom
    } else {
      //
    }
  };

  const handleFetchCompaniesList = async () => {
    const responseData = await fetchDiolkoCompaniesList(org._id);

    if (responseData && !responseData.error) {
      const companyList = responseData.orgs;
      const companyOptions = companyList.map((company) => {
        return {
          label: company.orgName,
          value: company.orgID,
        };
      });
      setCompaniesOption(companyOptions);
    }
  };

  const handleFetchDriverList = async () => {
    setIsLoading(true);

    const responseData = await fetchDriverList(
      filters,
      selectedOrgIDs,
      approvalStatus,
      false,
      isOnline,
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
    );

    if (responseData && !responseData.error) {
      setDriverList(responseData);
      setCurrentListCount(responseData.length);
    }
    // else {
    //   toaster("error", responseData.error.message);
    // }

    setIsLoading(false);
  };

  const handleFetchDriverDetails = async () => {
    const responseData = await fetchDriver(driverID);

    if (responseData && !responseData.error) {
      setDriverDetails(responseData);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleFetchDriverStats = async () => {
    const companyID = globalFilterValue?.companyIds ?? [];
    const lpIDs = globalFilterValue?.logisticsProviderIds ?? [];
    // const responseData = await fetchRidersStats(org._id, companyID, lpIDs);

    // if (responseData && !responseData.error) {
    //   setDriverStatCount({
    //     ...driverStatCount,
    //     ...responseData,
    //   });
    // }
  };

  const handleDeactivateDriver = async (profileID: string, toDelete: boolean, reason: string) => {
    const responseData = await driverActivation(profileID, toDelete, reason);

    if (responseData && !responseData.error) {
      toaster("success", t("toaster.deactivateDriverSucess"));
      handleFetchDriverList();
      getStatCount();
      history.push(`/driver-management/driver/`);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleDriverApproval = async (profileID: string, status: Statuses, reason?: string) => {
    const payload = omitBy(
      {
        profileID: profileID,
        approvalStatus: status,
        rejectedReason: reason,
      },
      (input) => input === "" || isNil(input),
    );
    const responseData = await driverApproval(payload);

    if (responseData && !responseData.error) {
      toaster("success", t("toaster.approvalUpdated"));
      handleFetchDriverList();
      getStatCount();
      history.push(`/driver-management/driver/`);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  /**
   * display different profile component based on driver/rider approval status
   * @returns driver profile component
   */
  const handleReturnDriverProfile = () => {
    if (!isEmpty(driverDetails) && canView) {
      const allowedDriverProfile = driverDetails.approvalStatus === DriverApprovalStatus.APPROVED || 
        (driverDetails.approvalStatus === DriverApprovalStatus.PENDING && driverDetails.status === 'PENDING_ACCOUNT')

      if (allowedDriverProfile) {
        return (
          <DriverProfile
            canEdit={canEdit}
            canDelete={canDelete}
            onBack={() => {
              history.goBack();
            }}
            driverDetails={driverDetails}
            onDeactivateDriver={handleDeactivateDriver}
          />
        );
      } else {
        return (
          <DriverProfileTwo
            canEdit={canEdit}
            onBack={() => {
              history.goBack();
            }}
            driverDetails={driverDetails}
            onDriverApproval={handleDriverApproval}
          />
        );
      }
    }
  };

  return (
    <div className="active-driver-listing">
      <Helmet>
        <title>{t("pageTitle.driver")}</title>
      </Helmet>

      <ModuleTitleBar
        titleText={t("title.driverManagement")}
        haveSubtitle
        subtitleText={t("title.driver")}
      />
      {id ? (
        handleReturnDriverProfile()
      ) : (
        <>
          <Row>
            {canView && (
              <Col xs={24} sm={12} md={16} lg={18} xl={20}>
                <div className="driver-statistics-group">
                  {driverStatistics.map((driverStat, index) => {
                    return (
                      <StatisticBox
                        key={index}
                        statObj={driverStat}
                        count={statResponse?.statistics[driverStat.name]}
                        context="Driver"
                        onClick={handleStatClick}
                        icon={driverStat.icon}
                        iconSrc={driverStat.iconSrc}
                      />
                    );
                  })}
                </div>
              </Col>
            )}

            {canCreate && (
              <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                <Row justify="end">
                  <div className="action-create-driver">
                    <Button
                      label={t("action.createDriver")}
                      icon={<RightCircleOutlined />}
                      type="primary"
                      onClick={() => {
                        handleCreateDriverAction();
                      }}
                    />
                  </div>
                  {/* SIMULATE FOR EXTERNAL */}
                  {/* ! Remove when it's not needed anymore, clarify with BA */}
                  <div className="action-create-driver">
                    <Button
                      label={t("action.createExternalDriver")}
                      icon={<RightCircleOutlined />}
                      type="primary"
                      onClick={() => {
                        handleCreateExternalDriverAction();
                      }}
                    />
                  </div>
                </Row>
              </Col>
            )}
          </Row>

          <Tabs
            destroyInactiveTabPane={true}
            onTabClick={handleTabClick}
            defaultActiveKey="0"
            activeKey={tabActiveKey}
            tabBarGutter={25}
            tabs={tabs /*ctrl click tabs to see its components */}
          />
        </>
      )}
      <div className="my-30">{paginationComponent}</div>
    </div>
  );
};

export default DriverListing;
